/**
 * useCursorParallax — ambient, performant cursor-driven shell parallax.
 *
 * Writes two CSS variables (--cursor-x, --cursor-y) to documentElement
 * so only paint (not React) updates. body::before translates based on
 * those vars. Honors prefers-reduced-motion. Skips on touch devices.
 */
import { useEffect } from 'react';

export function useCursorParallax({ strength = 12 } = {}) {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    if (window.matchMedia?.('(pointer: coarse)').matches) return;

    let rafId = 0;
    let nextX = 0, nextY = 0;
    const handleMove = (e) => {
      const w = window.innerWidth || 1;
      const h = window.innerHeight || 1;
      nextX = ((e.clientX / w) - 0.5) * strength;
      nextY = ((e.clientY / h) - 0.5) * strength * 0.6;
      if (!rafId) {
        rafId = requestAnimationFrame(() => {
          rafId = 0;
          document.documentElement.style.setProperty('--cursor-x', `${nextX.toFixed(2)}px`);
          document.documentElement.style.setProperty('--cursor-y', `${nextY.toFixed(2)}px`);
        });
      }
    };
    window.addEventListener('mousemove', handleMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', handleMove);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [strength]);
}
